import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, Warning } from "@phosphor-icons/react";

export default function AdminEsimReview() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["esim-review"],
    queryFn: async () => (await api.get("/admin/esim/manual-review")).data,
    refetchInterval: 20000,
  });
  const items = data?.items || [];
  const [busyId, setBusyId] = React.useState(null);

  const reprovision = async (sid) => {
    setBusyId(sid);
    try {
      const { data } = await api.post(`/admin/esim/${sid}/reprovision`);
      if (data.esim_provisioned) toast.success(`Re-provisioned (${data.result})`);
      else toast.error(`Still failing: ${data.provision_error || data.result}`);
      qc.invalidateQueries({ queryKey: ["esim-review"] });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-esim-review">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">eSIM Manual Review</h1>
        <p className="mt-1 text-sm text-muted-foreground">Paid eSIM orders whose provisioning needs attention. Re-provision is idempotent — it checks status first and never creates a duplicate order or charge.</p>
      </div>
      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.05] px-4 py-10 text-center text-sm text-emerald-500">All clear — no eSIM orders awaiting review.</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.session_id} data-testid={`esim-review-${it.session_id}`} className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold"><Warning size={16} weight="fill" className="text-amber-500" /> {it.email || "unknown"} · {it.country} · {it.data_gb}GB / {it.validity_days}d</p>
                  <p className="mt-1 text-xs text-muted-foreground">Plan {it.plan_id} · ${(it.amount_cents / 100).toFixed(2)} · {it.created_at?.slice(0, 16).replace("T", " ")}</p>
                  <p className="mt-1 break-words text-xs text-amber-600 dark:text-amber-400">Ref: {it.external_id || "—"} · {it.provision_error || "no error detail"}</p>
                </div>
                <button data-testid={`reprovision-${it.session_id}`} onClick={() => reprovision(it.session_id)} disabled={busyId === it.session_id}
                  className="flex shrink-0 items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                  <ArrowClockwise size={16} weight="bold" className={busyId === it.session_id ? "animate-spin" : ""} /> {busyId === it.session_id ? "Working…" : "Re-provision"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
